import React, { useMemo, useState, ChangeEvent } from 'react'
import { omit } from 'lodash-es'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { Box } from '@mui/material'
import { useField } from 'formik'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { TargetingCircle } from '../../../__generated__/graphql'
import { UpdateRadiusDialog } from '../../../pages/adgroup/settings/geoTargeting/UpdateRadiusDialog'
import { LocationTypesEnum } from '../../../pages/adgroup/settings/geoTargeting/GeoTargetingFields'
import { AutoCompleteGoogleMapFieldProps, PlaceType } from './intefaces'
import { useAutoComplete } from './useAutoComplete'
import { compactFormFieldSx } from '../DisplayedItem'
import { uppercaseFieldLabelSx } from '../styled'
import { getGeoLocationOptionLabel } from './geoLocationChipLabel'
import { getInlineGeoLocationsDisplay } from './geoLocationLimits'
import { renderGeoLocationChips } from './renderGeoLocationChips'
import GeoLocationChipList from './GeoLocationChipList'
import ViewAllGeoLocationsButton from './ViewAllGeoLocationsButton'
import GeoLocationsDialog from './GeoLocationsDialog'

const AutoCompleteGoogleMapField: React.FC<AutoCompleteGoogleMapFieldProps & { disableInlineCap?: boolean }> = ({
  name,
  label,
  required = false,
  locationType = LocationTypesEnum.Cities,
  countryCode = 'fr',
  compact = false,
  disableInlineCap = false,
}) => {
  const t = useAppTranslations()
  const [field, meta] = useField({ name })
  const { value } = field
  const locations = useMemo(() => (Array.isArray(value) ? value : []), [value])
  const { displayValue, exceedsInlineLimit, totalCount } = useMemo(
    () => getInlineGeoLocationsDisplay(locations),
    [locations]
  )
  const inlineCapped = !disableInlineCap && exceedsInlineLimit
  const stackChipsBelowInput = disableInlineCap
  const autocompleteValue = inlineCapped ? displayValue : locations

  const hasError = Boolean(meta.touched && meta.error)
  const helperText = (meta.error && hasError && t(meta.error)) || ' '
  const [radiusDialog, setRadiusDialog] = useState<{ open: boolean; cityFieldName: string }>({
    open: false,
    cityFieldName: '',
  })
  const [manageDialogOpen, setManageDialogOpen] = useState(false)
  const autocompleteId = disableInlineCap ? 'autoCompleteGoogleMapManage' : 'autoCompleteGoogleMap'
  const { options, onChange, setInputValue } = useAutoComplete(locationType, name, countryCode, autocompleteId)

  const showViewAllControl = !disableInlineCap && exceedsInlineLimit

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: showViewAllControl ? 'flex' : 'block',
          alignItems: 'flex-end',
          gap: 1,
          width: '100%',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Autocomplete
            id={autocompleteId}
            disabled={inlineCapped}
            getOptionLabel={(option) => getGeoLocationOptionLabel(option, locationType)}
            filterOptions={(x) => x}
            options={options}
            value={autocompleteValue}
            multiple
            onChange={(event: ChangeEvent<unknown>, newValue: Array<PlaceType | TargetingCircle>) => onChange(newValue)}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            renderValue={
              stackChipsBelowInput
                ? () => null
                : (tagValue, getItemProps) =>
                    renderGeoLocationChips(tagValue, getItemProps, locationType, {
                      fieldName: name,
                      onCityRadiusClick: (cityFieldName) => setRadiusDialog({ open: true, cityFieldName }),
                    })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                required={required}
                label={t(label)}
                variant={compact ? 'standard' : 'outlined'}
                fullWidth
                margin={compact ? 'none' : 'normal'}
                size="small"
                error={hasError}
                helperText={helperText}
                hiddenLabel={compact}
                sx={compact ? compactFormFieldSx : uppercaseFieldLabelSx}
              />
            )}
            renderOption={(props, option: PlaceType) => {
              const { key } = props as typeof props & { key?: React.Key }

              return (
                <Box component="li" key={key} {...omit(props, ['key', 'alignItems'])} sx={{ width: '100%' }}>
                  <Grid container sx={{ alignItems: 'center' }}>
                    <Grid size="grow">
                      {option?.structured_formatting?.main_text}
                      <Typography variant="body2" color="textSecondary">
                        {option?.structured_formatting?.secondary_text}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )
            }}
          />
        </Box>
        {showViewAllControl && (
          <ViewAllGeoLocationsButton count={totalCount} onClick={() => setManageDialogOpen(true)} />
        )}
      </Box>
      {stackChipsBelowInput && (
        <GeoLocationChipList
          locations={locations}
          locationType={locationType}
          fieldName={name}
          onCityRadiusClick={(cityFieldName) => setRadiusDialog({ open: true, cityFieldName })}
          onRemove={(index) => onChange(locations.filter((_, locationIndex) => locationIndex !== index))}
        />
      )}
      {showViewAllControl && (
        <GeoLocationsDialog
          open={manageDialogOpen}
          setOpen={setManageDialogOpen}
          mode="edit"
          name={name}
          label={label}
          locationType={locationType}
          countryCode={countryCode}
          count={totalCount}
        />
      )}
      {radiusDialog.open && (
        <UpdateRadiusDialog
          open={radiusDialog.open}
          cityFieldName={radiusDialog.cityFieldName}
          closeDialog={() => setRadiusDialog({ open: false, cityFieldName: '' })}
        />
      )}
    </Box>
  )
}

export default AutoCompleteGoogleMapField
